<template>
  <div class="dynamic-layout" :class="{ 'dynamic-layout__rtl': language === 'ar' }">
    <m-header class="dynamic-layout__header van-safe-area-top"  headerNameLogo="weelife-logo.png"
      :isShowBtn="true" :btnBackground="scssVariables.btnBg" :buttonText="$t('dynamic.header.openBtn')"
      :handleOpenApp="() => handleOpenApp(1)">
    </m-header>
    <div v-if="!error" class="dynamic-content">
      <div class="dynamic-content__user">
        <div class="dynamic-content__user--avatar">
          <img :src="dynamicData.portrait || portrait" alt="avatar">
        </div>
        <div class="dynamic-content__user--info">
          <span v-if="dynamicData.nickname" class="dynamic-content__info--name">{{ dynamicData.nickname }}</span>
          <span class="dynamic-content__info--time">{{
    dynamicData.releaseTime ? $formatToDateTime(dynamicData.releaseTime) : '' }}</span>
        </div>
        <div class="dynamic-content__user--follow">
          <Button class="dynamic-content__user--followBtn" plain round fr type="primary" color="#9A6BFF;"
            @click="handleOpenApp(2)">{{
    $t('dynamic.follow') }}
          </Button>
        </div>
      </div>
      <div class="dynamic-content__box">
        <div v-if="dynamicData.content" class="dynamic-content__text">
          <dynamic-clamp :value="dynamicData.content"></dynamic-clamp>
        </div>
        <div v-if="dynamicPicList.length > 0" class="dynamic-content__pics">
          <van-image v-for="(item, index) in dynamicPicList.slice(0, 4)" :key="index" class="dynamic-content__pics--img"
            :src="item.url" lazy-load show-error :fit="setImageFit()" :style="setImageStyle(item)"
            @click="handleImagePreview(dynamicPicList, index)"></van-image>
        </div>
        <div v-if="dynamicAudioList.length > 0" class="dynamic-content__audio">
          <van-image v-if="dynamicAudioCoverList.length > 0" class="dynamic-content__audio--img" fit="cover"
            :src="dynamicAudioCoverList[0].url" lazy-load show-error></van-image>
          <dynamic-audio class="dynamic-content__audio--player" :src="dynamicAudioList[0].url" @play="dynamicAudioPlay"
            @pause="dynamicAudioPause" @ended="dynamicAudioEnded"></dynamic-audio>
        </div>
      </div>
      <div class="dynamic-content__block">
      </div>
      <div class="dynamic-content__comment">
        <div class="dynamic-content__comment--title">{{ $t('dynamic.comment', { comments: dynamicData.comments || 0 })
          }}
        </div>
        <div class="dynamic-content__comment--placeholder">
          <span class="dynamic-content__comment--text">{{ $t('dynamic.openComment') }}</span>
        </div>
      </div>
    </div>
    <template v-else>
      <m-error :btnBackground="scssVariables.btnBg" :btnTextColor="scssVariables.btnText" class="bgc-error"
        :content="$t('dynamic.empty.content')" picture="dynamic/no-permission-replace.png" :isShowBtn="false"></m-error>
    </template>
    <div class="dynamic-footer">
      <Button class="dynamic-footer__open" fr type="primary" :color="scssVariables.btnBg"
        :style="{ color: scssVariables.btnText }" @click="handleOpenApp(3)">{{ $t('dynamic.openApp') }}
      </Button>
    </div>
    <m-dialog-v2 :qrCodeUrl="qrCodeUrl" :headerText="$t('dynamic.components.MDialog.headerText')"
      :btnText="$t('dynamic.components.MDialog.btnText')" :show="isShowMDialog" :onClose="() => isShowMDialog = false"
      :onDownload="handleDownLoad"></m-dialog-v2>
  </div>
</template>

<script lang='ts'>
import { MHeader, MDialogV2, MError } from '@/pages/components/index'
import { getRequestLanguage } from '@/api/utils'
import { defineComponent, reactive, toRefs, computed, onBeforeMount, onMounted, nextTick, defineAsyncComponent } from 'vue'
import scssVariables from '@/utils/scssVariables'
import { Button, Toast, Image as VanImage, ImagePreview } from 'vant'
import { autoRem } from '@/utils/remUtils'
import portrait from '@/assets/img/default_portrait.png'
import { DynamicDataSucType, DynamicMediaItemType } from '@/api/types/dynamic'
import { useI18n } from 'vue-i18n'
import { useCallApp } from '@/hooks/useCallApp'
import UaParser from 'ua-parser-js'
import { isIpad } from '@/utils/is'
import { isInAppUA } from '@/utils/uaConfig'
import { getDynamicData } from '@/api/request/dynamic'
import { handleDownLoadWeelife3d } from '@/utils/file/download'
import { postSLSReportData } from '@/api/request/sls'
import { urlParam2Obj } from '@/utils/url'
import { useClipboardV2 } from '@/hooks/useClipboardV2'
import { processDynamicContent } from '../utils/dynamic'

interface IDynamicState {
  qrCodeUrl: string
  isShowMDialog: boolean
  dynamicData: DynamicDataSucType
  portrait: string
  userId: string
  contentId: string
  dynamicPicList: DynamicMediaItemType[]
  error: boolean
  dynamicAudioList: DynamicMediaItemType[]
  dynamicAudioCoverList: DynamicMediaItemType[]
}

enum DynamicButtonType {
  Play = 1,
  Follow = 2,
  OpenAppForMoreContent = 3,
}

export default defineComponent({
  name: 'Dynamic',
  components: {
    MHeader,
    MDialogV2,
    Button,
    MError,
    VanImage,
    DynamicClamp: defineAsyncComponent(() => import('../components/dynamic/dynamic-clamp.vue')),
    DynamicAudio: defineAsyncComponent(() => import('../components/dynamic/dynamic-audio.vue'))
  },
  setup() {
    const { t } = useI18n()
    const state = reactive<IDynamicState>({
      qrCodeUrl: `${window.location.href}?toPath=true`,
      isShowMDialog: false,
      portrait,
      userId: '',
      contentId: '',
      dynamicData: {} as DynamicDataSucType,
      dynamicPicList: [] as DynamicMediaItemType[],
      dynamicAudioList: [] as DynamicMediaItemType[],
      dynamicAudioCoverList: [] as DynamicMediaItemType[],
      error: false
    })
    const uaParser = new UaParser()
    const uaInfo = uaParser.getResult()
    autoRem(true)

    const language = computed(() => {
      return getRequestLanguage()
    })

    const setImageFit = () => {
      if (state.dynamicPicList.length >= 4) {
        return 'cover'
      }
      return 'contain'
    }

    const setImageStyle = (item: DynamicMediaItemType) => {
      const baseStyle = {
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        marginRight: '0px'
      }
      const { width, height } = item
      if (height > width * 2) {
        baseStyle.height = `${height * 0.3}px`
      }

      if (state.dynamicPicList.length >= 4) {
        baseStyle.width = 'calc(50% - 10px)'
        baseStyle.height = '120px'
      }

      return baseStyle
    }
    onBeforeMount(() => {
      const { userId, contentId } = urlParam2Obj()
      state.userId = userId || ''
      state.contentId = contentId || ''
      if (!state.userId || !state.contentId) {
        state.error = true
        return
      }
      getDynamicData(state.userId, state.contentId).then(res => {
        if (res && res.data) {
          state.dynamicData = res.data
          state.dynamicData.content = res.data.content ? processDynamicContent(res.data.content) : res.data.content
          state.dynamicAudioList = state.dynamicData.mediaList.length > 0 ? state.dynamicData.mediaList.filter(item => item.type === 2 && item.url) : []
          if (state.dynamicAudioList.length === 0) {
            state.dynamicPicList = state.dynamicData.mediaList.length > 0 ? state.dynamicData.mediaList.filter(item => item.type === 1 && item.url) : []
          } else if (state.dynamicAudioList.length > 0) {
            state.dynamicAudioCoverList = state.dynamicData.mediaList.length > 0 ? state.dynamicData.mediaList.filter(item => item.type === 1 && item.url) : []
          }
          return
        }
        state.error = true
      }).catch(err => {
        state.error = true
        console.error(err)
      })
    })

    onMounted(() => {
      postSLSReportData([{ eventName: 'post_show_web', customData: { author_uid: state.userId, post_id: state.contentId } }])
      nextTick(() => {
        handleCopyCode()
      })
    })

    const { clipboardCopy } = useClipboardV2()

    const handleCopyCode = () => {
      return new Promise((resolve) => {
        // 使用 copyText
        clipboardCopy(window.location.href, undefined, (event, status) => {
          if (status === 'success') {
            console.log('复制成功', window.location.href)
            resolve(true)
          } else {
            console.error('复制失败')
            resolve(false)
          }
        })
      })
    }

    const handleImagePreview = (list: any[], index: number) => {
      if (list.length === 0) {
        return
      }
      const imgList = list.map(item => item.url)
      ImagePreview({ images: [...imgList], startPosition: index })
    }

    const handleOpenApp = (type: DynamicButtonType) => {
      postSLSReportData([{ eventName: 'post_click_web', customData: { button_type: type } }])
      handleCopyCode()
      if (
        uaInfo.browser.name === 'WeChat' ||
        uaInfo.browser.name === 'Weibo'
      ) {
        state.isShowMDialog = true
        return
      }
      // 手机百度浏览器因官方封禁，绕不过去，需加白名单
      // 判断是否在app内打开（暂时不能走加入房间的逻辑）
      if (uaInfo.browser.name === 'baiduboxapp' || isInAppUA(uaInfo.ua)) {
        state.isShowMDialog = true
        return
      }

      /** 判断是否为ipad、android、ios */
      if (isIpad() || uaInfo.os.name === 'Android' || uaInfo.os.name === 'iOS') {
        handleCallApp()
        return
      }

      if (uaInfo.os.name !== 'Android' && uaInfo.os.name !== 'iOS') {
        state.isShowMDialog = true
      }
    }

    const handleDownLoad = () => {
      handleDownLoadWeelife3d()
    }

    // 唤起APP
    const handleCallApp = () => {
      postSLSReportData([{ eventName: 'post_open_app_by_web', customData: {} }])
      const options = {
        path: 'player.dynamic',
        param: {
          ...urlParam2Obj()
        }
      }
      useCallApp(options, (status) => {
        if (status === 'pending') {
          Toast.loading({
            message: `${t('public.wait')}`,
            forbidClick: true,
            duration: 3000
          })
        }
        if (status === 'failure') {
          state.isShowMDialog = true
        }

        if (status !== 'pending') {
        }
      })
    }

    const dynamicAudioPlay = () => {
      postSLSReportData([{ eventName: 'post_click_web', customData: { button_type: 4 } }])
    }

    const dynamicAudioPause = (duration: number, currentTime: number) => {
      const customData = {
        author_uid: state.userId,
        post_id: state.contentId,
        duration_time: Math.floor(currentTime) | 0, // 播放时长
        voice_time: Math.floor(duration) | 0 // 总时长
      }
      postSLSReportData([{ eventName: 'post_click_web_voice_pause', customData: { ...customData } }])
    }

    const dynamicAudioEnded = (duration: number, currentTime: number) => {
      const customData = {
        author_uid: state.userId,
        post_id: state.contentId,
        pause_type: currentTime >= duration ? 1 : 2, // int:pause_type(音频播放停止类型)1=自动播放完成；2=被动终止；
        duration_time: Math.floor(currentTime) | 0, // 播放时长
        voice_time: Math.floor(duration) | 0 // 总时长
      }
      postSLSReportData([{ eventName: 'post_voice_done_web', customData: { ...customData } }])
    }

    return {
      scssVariables,
      ...toRefs(state),
      language,
      setImageFit,
      setImageStyle,
      handleImagePreview,
      handleOpenApp,
      handleDownLoad,
      dynamicAudioPlay,
      dynamicAudioPause,
      dynamicAudioEnded
    }
  }
})
</script>
<style lang='scss' scoped>
@import './index.scss';
.van-safe-area-top{
background-image: url('../../assets/img/topic/bg_web_topic.png');
}
.dynamic-content__info--name{
  color: #FFFFFF;
}
.dynamic-content__info--time{
  color: #646464;
}
:deep(.dynamic-clamp__hidden){
  color: #E4E4E4;
}
.dynamic-content__block{
  background: transparent;
  background-image: url('../../assets/img/topic/bg_web_topic.png');
}
.bgc-error{
  background: none;
  background-color: #1a1a1a;
  top: 0;
}
</style>
